import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { cx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import InlineEdit from '@atlaskit/inline-edit';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Select, { type ValueType } from '@atlaskit/select';
import { layers } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import ListWithPopup from '@atlassian/list-with-popup/ListWithPopup.tsx';
import { TagItem, TagItemPopupContentWrapper } from '@atlassian/list-with-popup/TagItem.tsx';

import type { FieldBaseProps } from '../common/types';
import { useValidateRequiredField } from '../hooks/useValidateRequiredField';

import type { JiraMultipleSelectField$key } from './__generated__/JiraMultipleSelectField.graphql';
import { ValidationPopup } from './ValidationPopup';

const messages = defineMessages({
	showMoreTooltip: {
		id: 'jira.platform-fields.multi-select.show-more',
		defaultMessage: 'Show more',
		description: 'Tooltip for the show more items button',
	},
	none: {
		id: 'jira.jira-platform-fields.multi-select.none',
		defaultMessage: 'None',
		description: 'Value when no options are selected',
	},
	isRequiredError: {
		id: 'jira.jira-platform-fields.multi-select.is-required-error',
		defaultMessage: '{fieldName} is required',
		description: 'Error message for required fields',
	},
});

const styles = cssMap({
	container: {
		width: '100%',
		// The InlineEdit component has a default 8px margin top, so we are negating this here
		marginTop: token('space.negative.100'),
	},
	readView: {
		width: '100%',
		height: '40px',
		display: 'flex',
		alignItems: 'center',
		paddingRight: token('space.100'),
		paddingLeft: token('space.100'),
	},
	editableReadView: {
		cursor: 'pointer',
		height: '36px', // 40px - 2 * 2px border
	},
});

type Option = { label: string; value: string };

export type Props = FieldBaseProps & {
	field: JiraMultipleSelectField$key;
	onChange?: (newValue: string[] | null) => void;
};

const ReadView = memo(({ options, isEditable }: { isEditable: boolean; options: Option[] }) => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={cx(styles.readView, isEditable && styles.editableReadView)}>
			{options.length === 0 ? (
				<Text align="start" color="color.text.subtle" maxLines={1}>
					{formatMessage(messages.none)}
				</Text>
			) : (
				<ListWithPopup
					items={options.map((opt) => ({
						key: opt.value,
						name: opt.label,
						isColored: false,
					}))}
					ItemComponent={TagItem}
					PopupContentWrapper={TagItemPopupContentWrapper}
					showMoreTooltip={formatMessage(messages.showMoreTooltip)}
					isHoverPopoverEnabled
				/>
			)}
		</Box>
	);
});

export const JiraMultipleSelectField: React.MemoExoticComponent<
	({ field, fieldValue, isFieldEditable, onChange }: Props) => JSX.Element | null
> = memo(({ field, fieldValue, isFieldEditable = true, onChange }: Props): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const data = useFragment<JiraMultipleSelectField$key>(
		graphql`
			fragment JiraMultipleSelectField on JiraMultipleSelectField {
				name
				fieldConfig {
					isRequired
					isEditable
				}
				fieldOptions {
					edges {
						node {
							value
						}
					}
				}
				selectedOptions {
					edges {
						node {
							value
						}
					}
				}
			}
		`,
		field,
	);

	const fieldName = data?.name;

	const isEditable = !!data?.fieldConfig?.isEditable && isFieldEditable;

	const isRequired = !!data?.fieldConfig?.isRequired;

	const validate = useValidateRequiredField({
		isRequired,
		isEditable,
		fieldName,
		fieldTypeForAnalytics: 'MultipleSelectField',
	});

	const allowedOptions: Option[] = useMemo(() => {
		const edges = data?.fieldOptions?.edges;
		return (
			edges?.filter(Boolean).map((edge) => {
				const value = String(edge?.node?.value ?? '');
				return { label: value, value };
			}) ?? []
		);
	}, [data?.fieldOptions?.edges]);

	const defaultSelectedValues: string[] = useMemo(() => {
		const edges = data?.selectedOptions?.edges;
		return (
			edges
				?.filter(Boolean)
				.map((edge) => String(edge?.node?.value ?? ''))
				.filter(Boolean) ?? []
		);
	}, [data?.selectedOptions?.edges]);

	const currentValues: string[] = useMemo(() => {
		if (!Array.isArray(fieldValue)) {
			return [];
		}
		const allowedSet = new Set(allowedOptions.map((o) => o.value.toLowerCase()));
		const filteredValues = fieldValue.filter((v) => allowedSet.has(v.toLowerCase()));

		// If some invalid values were filtered out, tell the upstream
		if (filteredValues.length !== fieldValue.length) {
			onChange?.(filteredValues);
		}
		return filteredValues;
	}, [fieldValue, allowedOptions, onChange]);

	const selectedOptions: Option[] = useMemo(() => {
		const map = new Map(allowedOptions.map((o) => [o.value.toLowerCase(), o]));
		return currentValues
			.map((v) => map.get(String(v).toLowerCase()) ?? { label: String(v), value: String(v) })
			.filter(Boolean);
	}, [allowedOptions, currentValues]);

	const handleOnConfirm = useCallback(
		(newValue: ValueType<Option, true>) => {
			const values = (newValue ?? []).map((opt) => String((opt as Option).value)).filter(Boolean);
			onChange?.(values.length ? values : null);
		},
		[onChange],
	);

	const readView = useMemo(
		() => <ReadView options={selectedOptions} isEditable={isEditable} />,
		[selectedOptions, isEditable],
	);

	// Allows us to make sure we only fall back to default value once, on initial render
	const isFirstRenderRef = useRef(true);
	useEffect(() => {
		// If the passed down value is empty and a default value is available, tell the upstream
		if (
			currentValues.length === 0 &&
			defaultSelectedValues.length > 0 &&
			isFirstRenderRef.current === true
		) {
			onChange?.(defaultSelectedValues);
		}
		isFirstRenderRef.current = false;
	}, [currentValues, defaultSelectedValues, onChange]);

	if (!isEditable) {
		return readView;
	}

	return (
		<Box xcss={styles.container}>
			<InlineEdit<ValueType<Option, true>>
				defaultValue={selectedOptions}
				readView={() => readView}
				editView={({ errorMessage, isInvalid, ...fieldProps }) => (
					<ValidationPopup
						errorMessage={errorMessage}
						showInvalidPopup={Boolean(isInvalid && errorMessage)}
						trigger={({ ref }) => (
							<Box ref={ref}>
								<Select<Option, true>
									{...fieldProps}
									menuPortalTarget={
										fg('gravityai-4581-jira-aifc-iframe-korosu') ? undefined : document.body
									}
									menuPosition={fg('gravityai-4581-jira-aifc-iframe-korosu') ? 'fixed' : 'absolute'}
									menuPlacement="auto"
									styles={
										fg('gravityai-4581-jira-aifc-iframe-korosu')
											? {
													menu: (base) => ({ ...base, zIndex: layers.modal() }),
													menuPortal: (base) => ({ ...base, zIndex: layers.modal() }),
												}
											: undefined
									}
									label={fieldName}
									isMulti
									autoFocus
									options={allowedOptions}
									getOptionValue={(o) => o.value}
									getOptionLabel={(o) => o.label}
									placeholder={formatMessage(messages.showMoreTooltip)}
									isInvalid={isInvalid}
								/>
							</Box>
						)}
					/>
				)}
				validate={validate}
				onConfirm={handleOnConfirm}
				hideActionButtons
				readViewFitContainerWidth
				editButtonLabel={selectedOptions.map((o) => o.label).join(', ') ?? undefined}
			/>
		</Box>
	);
});
